/**
 * Claim Selection Utilities
 *
 * Utilities for managing claim selection in RPM components.
 * Handles all-records-selected and excluded-rows patterns.
 */

import { UnifiedClaim } from '../../../interfaces/billing.rpm';

/**
 * Gets the list of claims to export based on selection state
 *
 * @param data - Full dataset of claims
 * @param selectedClaims - Array of selected claim IDs
 * @param allRecordsSelected - Whether all records are selected
 * @param excludedRows - Rows excluded when all records are selected
 * @param filterFn - Optional filter function to apply to selected claims
 * @returns Array of claims to export
 */
export function getSelectedClaimsToExport(
  data: UnifiedClaim[],
  selectedClaims: number[],
  allRecordsSelected: boolean,
  excludedRows: { id: number; index: number }[],
  filterFn?: (claim: UnifiedClaim) => boolean
): UnifiedClaim[] {
  let claimsToExport: UnifiedClaim[];

  if (allRecordsSelected) {
    // Export all claims except excluded ones
    claimsToExport = data.filter(claim => !excludedRows.some(row => row.id === claim.id));
  } else {
    // Export only selected claims
    claimsToExport = data.filter(claim => selectedClaims.includes(claim.id));
  }

  // Apply additional filter if provided (e.g., exportable status filter)
  if (filterFn) {
    claimsToExport = claimsToExport.filter(filterFn);
  }

  return claimsToExport;
}

/**
 * Gets the list of claim IDs to export based on selection state
 *
 * @param data - Full dataset of claims
 * @param selectedClaims - Array of selected claim IDs
 * @param allRecordsSelected - Whether all records are selected
 * @param excludedRows - Rows excluded when all records are selected
 * @param filterFn - Optional filter function to apply to selected claims
 * @returns Array of claim IDs to export
 */
export function getSelectedClaimIdsToExport(
  data: UnifiedClaim[],
  selectedClaims: number[],
  allRecordsSelected: boolean,
  excludedRows: { id: number; index: number }[],
  filterFn?: (claim: UnifiedClaim) => boolean
): number[] {
  const claims = getSelectedClaimsToExport(
    data,
    selectedClaims,
    allRecordsSelected,
    excludedRows,
    filterFn
  );
  return claims.map(claim => claim.id);
}

/**
 * Calculates the total number of selected claims
 *
 * @param totalCount - Total count from pagination
 * @param selectedClaims - Array of selected claim IDs
 * @param allRecordsSelected - Whether all records are selected
 * @param excludedRows - Rows excluded when all records are selected
 * @returns Total number of selected claims
 */
export function getTotalSelectedCount(
  totalCount: number,
  selectedClaims: number[],
  allRecordsSelected: boolean,
  excludedRows: { id: number; index: number }[]
): number {
  return allRecordsSelected ? (totalCount - excludedRows.length) : selectedClaims.length;
}
